import { getResponseCMS } from '@/api/cms/base';
import { camelize } from '@/helpers/common';
import { CACHE_REVALIDATE_TIMEOUT, CLIENT_ENVS } from '@/helpers/constants';
import { handleSignOutEvent } from '@/modules/common/helpers/handleSignOutEvent';
import { isClient } from '../server/helpers-client';
import { CLIENT_BASE_URL, REVALIDATE, SERVER_BASE_URL } from './helpers';

interface IFetcher {
  baseURL: string;
  headers?: HeadersInit;
  revalidate?: number;
  transformResponse?: (data: any) => any;
}

export const fetcher =
  ({
    baseURL,
    headers,
    revalidate = CACHE_REVALIDATE_TIMEOUT,
    transformResponse,
  }: IFetcher) =>
  async <T = any>(...args: Parameters<typeof fetch>): Promise<T> => {
    try {
      const [pathname, config] = args;
      const url = `${baseURL}${pathname}`;
      const mergedHeaders = new Headers(config?.headers);
      Object.entries(headers || {}).forEach(([key, value]) =>
        mergedHeaders.set(key, value)
      );
      const response = await fetch(url, {
        next: { revalidate },
        ...config,
        headers: mergedHeaders,
        credentials: 'include',
      });
      handleSignOutEvent(
        typeof pathname === 'string' ? pathname.replace(/^\//, '') : '',
        response.headers
      );
      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const text = await response.text();
      if (!text) return null as T;

      const json = JSON.parse(text);
      const transformedResponse = transformResponse
        ? transformResponse(json)
        : json;
      return camelize(transformedResponse) as T;
    } catch (error) {
      console.log('Error in fetcher:', error);
      throw error;
    }
  };

const ssConfig = {
  baseURL: isClient() ? CLIENT_BASE_URL : SERVER_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  ...(isClient() ? {} : { revalidate: REVALIDATE }),
};

export const fetcherCMS = fetcher({
  baseURL: CLIENT_ENVS.CMS_URL || '',
  transformResponse: getResponseCMS,
  revalidate: REVALIDATE,
});

export const fetcherSS = fetcher({
  ...ssConfig,
  headers: { ...ssConfig.headers, Accept: 'application/vnd.s.v1+json' },
});

export const fetcherSSV2 = fetcher({
  ...ssConfig,
  headers: { ...ssConfig.headers, Accept: 'application/vnd.s.v2+json' },
});

export const fetcherInternal = fetcher({
  baseURL: '/api/internal',
  headers: {
    'Content-Type': 'application/json',
  },
});
